// Lift path utilities — PURE module shared by the game server (require)
// and the WebXR client (esbuild import). Keep it dependency-free.
//
// The funicular ride lines are the WORLD stage's solved funicular runs
// (lifts.json `lines`, pipeline/bake2.py _ship_lifts); the aerial lines are
// the bake's solved cable supports (`aerial`, bake.py bake_lifts). Both are
// PURE functions of lifts.json, so the server and the client derive the
// same cars and cabins.
'use strict';

function dist3(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

// -> array of { name, points [[x,y,z]...], cum, length, bore?, bridge?, deck? },
// longest first
function funicularLines(liftsJson) {
  if (!Array.isArray(liftsJson.lines)) {
    throw new Error('lifts.json has no `lines` (the WORLD stage\'s funicular runs) — re-bake this world');
  }
  return liftsJson.lines
    .filter((l) => l.pts && l.pts.length >= 2)
    .map((l) => {
      const cum = [0];
      for (let i = 1; i < l.pts.length; i++) cum.push(cum[i - 1] + dist3(l.pts[i - 1], l.pts[i]));
      return { name: l.name || 'Funicular', points: l.pts, cum,
        length: cum[cum.length - 1], bore: l.bore || null,
        bridge: l.bridge || null, deck: l.deck || null };
    })
    .sort((a, b) => b.length - a.length);
}

// ---------------------------------------------------------------- aerialways
// Cable cars (Pendelbahn: two counterweighted cabins, one up while one comes
// down) and gondolas (Umlaufbahn: many cabins circulating on a loop). Like the
// funicular ride line, the geometry is a PURE function of lifts.json so the
// server's beam validation and the client's rendering derive the exact same
// cabin positions — there is deliberately no heightfield input here.
//
// lifts.json.aerial: [{name, kind, sup: [[x, topY, z, baseY], ...]}] where
// sup are the cable saddle points (stations first/last, pylons between) with
// terrain clearance already solved against the shipped ground.
// saddle/pylon/sag MUST stay numerically identical to bake.py AERIAL_P (which
// additionally holds the clearance envelope) — the bake solves the catenary
// these numbers draw. `gauge`/`drop`/`space` are render/board-side only.
// `space` = metres of loop per cabin (circulating cabin count = 2*length/space).
const AERIAL = {
  cable_car: { gauge: 4.2, drop: 4.6, saddle: 6.2, pylon: 16, sag: 0.012, space: 170 },
  gondola: { gauge: 2.7, drop: 2.9, saddle: 4.6, pylon: 12, sag: 0.02, space: 170 },
  // open chairs: origin at the footrest, seat hangs 2.2 m under the rope;
  // low pylons, droopier rope, chairs every ~27 m of line (55 m of loop)
  chair_lift: { gauge: 5.0, drop: 2.2, saddle: 3.4, pylon: 9.0, sag: 0.025, space: 55 },
};

function _mkPath(points) {
  const cum = [0];
  for (let i = 1; i < points.length; i++) cum.push(cum[i - 1] + dist3(points[i - 1], points[i]));
  return { points, cum, length: cum[cum.length - 1] };
}

// sagged, laterally offset cable polyline through the support saddles.
// side = +1 | -1 (the two cables of the system: up-track and down-track).
function _cablePath(sup, side, gauge, sagK) {
  // averaged horizontal perpendicular per support (like frames() client-side)
  const perp = sup.map((s, i) => {
    const a = sup[Math.max(0, i - 1)], b = sup[Math.min(sup.length - 1, i + 1)];
    const dx = b[0] - a[0], dz = b[2] - a[2];
    const l = Math.hypot(dx, dz) || 1;
    return [-dz / l, dx / l];
  });
  const off = sup.map((s, i) => [s[0] + perp[i][0] * gauge * 0.5 * side, s[1],
    s[2] + perp[i][1] * gauge * 0.5 * side]);
  const pts = [];
  for (let i = 0; i < off.length - 1; i++) {
    const a = off[i], b = off[i + 1];
    const span = Math.hypot(b[0] - a[0], b[2] - a[2]);
    const sag = Math.min(14, Math.max(0.35, span * sagK));
    const n = Math.max(2, Math.ceil(span / 12));
    for (let k = 0; k < n; k++) {
      const t = k / n;
      pts.push([a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t - sag * 4 * t * (1 - t),
        a[2] + (b[2] - a[2]) * t]);
    }
  }
  pts.push(off[off.length - 1].slice());
  return _mkPath(pts.map((p) => [Math.round(p[0] * 100) / 100,
    Math.round(p[1] * 100) / 100, Math.round(p[2] * 100) / 100]));
}

// -> [{ name, kind, system ('pendel'|'circulating'), sides: [path, path],
//       sup, cabDrop, gauge, cabins, length }]
// `livery` (bake livery.json, optional) may override system/cabin count per
// line — BOTH ends must pass the same livery object or cabin positions drift.
function aerialLines(liftsJson, livery) {
  if (!Array.isArray(liftsJson && liftsJson.aerial)) {
    throw new Error('lifts.json has no `aerial` (the solved cable supports) — re-bake this world');
  }
  const src = liftsJson.aerial.filter((a) => a.sup && a.sup.length >= 2);
  const lines = [];
  for (const a of src) {
    const P = AERIAL[a.kind] || AERIAL.gondola;
    const sides = [_cablePath(a.sup, 1, P.gauge, P.sag),
      _cablePath(a.sup, -1, P.gauge, P.sag)];
    if (sides[0].length < 250) continue;
    // chair_lift ignores livery system/cabins entirely: the livery pass never
    // judged chairs, and a stray hint would move cabin positions — this guard
    // lives HERE so both ends stay identical by construction.
    const chair = a.kind === 'chair_lift';
    const prof = chair ? null : (livery && livery.lines && livery.lines[a.name]);
    const system = (prof && (prof.system === 'pendel' || prof.system === 'circulating'))
      ? prof.system : (a.kind === 'cable_car' ? 'pendel' : 'circulating');
    let cabins = 2;
    if (system === 'circulating') {
      const hint = prof && Number.isFinite(prof.cabins) ? prof.cabins : 0;
      const lo = chair ? 6 : 4, hi = chair ? 96 : 48;
      cabins = Math.max(lo, Math.min(hi,
        hint >= 4 ? Math.round(hint) : Math.round(2 * sides[0].length / P.space)));
    }
    lines.push({ name: a.name || a.kind, kind: a.kind, system, sides, sup: a.sup,
      cabDrop: P.drop, gauge: P.gauge, cabins, length: sides[0].length });
  }
  lines.sort((x, y) => y.length - x.length);
  return lines;
}

// Cabin k's FLOOR position for line state s. Pendel: s in [0,1], cabin 0 rides
// the up-track at s, cabin 1 the down-track at 1-s (counterweighted). Circulating:
// s is the loop phase; cabin k sits at phase (s + k/cabins) mod 1 — first half
// of the loop ascends side 0, second half descends side 1.
// `out` (optional, the client's per-frame cabin loop): filled and returned
// instead of a fresh object, same values.
function aerialCabinPos(line, s, k, out) {
  let side, sv, dir;
  if (line.system === 'pendel') {
    side = k ? 1 : 0;
    sv = k ? 1 - s : s;
    dir = k ? -1 : 1;
  } else {
    const ph = ((s + k / line.cabins) % 1 + 1) % 1;
    if (ph <= 0.5) { side = 0; sv = ph * 2; dir = 1; } else { side = 1; sv = 2 - ph * 2; dir = -1; }
  }
  if (out) {
    samplePath(line.sides[side], sv, out);
    out.cableY = out.pos[1];
    out.pos[1] -= line.cabDrop;
    out.side = side; out.sv = sv; out.dir = dir;
    return out;
  }
  const { pos, tangent } = samplePath(line.sides[side], sv);
  return { pos: [pos[0], pos[1] - line.cabDrop, pos[2]],
    cableY: pos[1], tangent, side, sv, dir };
}

// s in [0,1] -> { pos: [x,y,z], tangent: [x,y,z] }; `out` (optional) =
// { pos, tangent } arrays to fill instead of allocating
function samplePath(path, s, out) {
  const d = Math.max(0, Math.min(1, s)) * path.length;
  const cum = path.cum, pts = path.points;
  let i = 1;
  while (i < cum.length - 1 && cum[i] < d) i++;
  const t = (d - cum[i - 1]) / Math.max(1e-6, cum[i] - cum[i - 1]);
  const a = pts[i - 1], b = pts[i];
  const len = dist3(a, b) || 1;
  if (out) {
    const p = out.pos, g = out.tangent;
    p[0] = a[0] + (b[0] - a[0]) * t; p[1] = a[1] + (b[1] - a[1]) * t; p[2] = a[2] + (b[2] - a[2]) * t;
    g[0] = (b[0] - a[0]) / len; g[1] = (b[1] - a[1]) / len; g[2] = (b[2] - a[2]) / len;
    return out;
  }
  const pos = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  const tangent = [(b[0] - a[0]) / len, (b[1] - a[1]) / len, (b[2] - a[2]) / len];
  return { pos, tangent };
}

module.exports = { funicularLines, samplePath, dist3,
                   aerialLines, aerialCabinPos, AERIAL };
